import React, { FC, ReactElement, useState } from "react";
import { AssetBundle, Mutator, MutatorProperty } from "../../../models/types";
import { Alert, Card, Col, Collapse, Row } from "react-bootstrap";
import { AssetbundleCard } from "../../cards/mutator/AssetbundleCard";
import { MutatorNumberCard } from "../../cards/mutator/MutatorNumberCard";
import { MutatorStringCard } from "../../cards/mutator/MutatorStringCard";
import { CustomTypeObjectCard } from "../../cards/mutator/CustomTypeObjectCard";
import { CustomTypeEnumCard } from "../../cards/mutator/CustomTypeEnumCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import styles from "./MutatorView.module.scss";

interface Props {
    mutatorConfig: Mutator | undefined;
    assetBundles: AssetBundle[];
    showMetaGlobal: boolean;
    onChange: (name: string, value: any) => void;
}

const MutatorOptionsImplementation: FC<Props> = ({
    mutatorConfig,
    assetBundles,
    showMetaGlobal,
    onChange
}) => {
    if (!mutatorConfig) {
        return null;
    }

    const getVariantByType = (type: string) => {
        switch (type) {
            case "assetbundle":
                return "info";
            case "string":
                return "demoted";
            case "number":
                return "density-2";
            default:
                if (type.includes("@")) {
                    return "misc";
                } else if (type.includes("$")) {
                    return "analytics-alt";
                }
                return "warning";
        }
    };

    const categoryMap = new Map<string, MutatorProperty[]>();
    mutatorConfig.properties?.forEach(property => {
        const category = property.meta?.category ?? "Options";
        if (!categoryMap.has(category)) {
            categoryMap.set(category, []);
        }
        categoryMap.get(category)?.push(property);
    });

    const categories = Array.from(categoryMap.entries());

    const [openCategories, setOpenCategories] = useState<
        Record<string, boolean>
    >({});

    const toggleCategory = (name: string) => {
        setOpenCategories(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };

    const renderMutatorProperty = (
        property: MutatorProperty,
        index: number
    ): ReactElement => {
        switch (property.type) {
            case "number":
                return (
                    <MutatorNumberCard
                        key={index}
                        property={property}
                        showMetaGlobal={showMetaGlobal}
                        getVariantByType={getVariantByType}
                        onChange={onChange}
                    />
                );
            case "string":
                return (
                    <MutatorStringCard
                        key={index}
                        property={property}
                        showMetaGlobal={showMetaGlobal}
                        getVariantByType={getVariantByType}
                        onChange={onChange}
                    />
                );
            case "assetbundle": {
                return (
                    <AssetbundleCard
                        key={index}
                        property={property}
                        assetbundles={assetBundles}
                        showMetaGlobal={showMetaGlobal}
                        getVariantByType={getVariantByType}
                        onChange={onChange}
                    />
                );
            }
            default: {
                if (property.type.includes("@")) {
                    return (
                        <CustomTypeEnumCard
                            key={index}
                            property={property}
                            enums={mutatorConfig.enums}
                            showMetaGlobal={showMetaGlobal}
                            getVariantByType={getVariantByType}
                            onChange={onChange}
                        />
                    );
                } else if (property.type.includes("$")) {
                    const customType = mutatorConfig.types[property.type];
                    return (
                        <CustomTypeObjectCard
                            key={index}
                            property={property}
                            assetbundles={assetBundles}
                            enums={mutatorConfig.enums}
                            customType={customType}
                            showMetaGlobal={showMetaGlobal}
                            getVariantByType={getVariantByType}
                            onChange={onChange}
                        />
                    );
                }
                return (
                    <>
                        <Alert variant="danger" key={index}>
                            Unknown Type: {property.type}
                        </Alert>
                    </>
                );
            }
        }
    };

    const renderPropertyRow = (properties: MutatorProperty[]) => (
        <Row className="d-flex justify-content-start">
            {properties.map((property, index) => (
                <Col key={index} xs="auto">
                    {renderMutatorProperty(property, index)}
                </Col>
            ))}
        </Row>
    );

    const renderPropertyColumn = (properties: MutatorProperty[]) => (
        <>
            {properties.map((property, index) => (
                <Row key={index} className="d-flex justify-content-start">
                    <Col xs="auto">
                        {renderMutatorProperty(property, index)}
                    </Col>
                </Row>
            ))}
        </>
    );

    const renderCategoryBody = (properties: MutatorProperty[]) => {
        const numberProps: MutatorProperty[] = [];
        const stringProps: MutatorProperty[] = [];
        const assetbundleProps: MutatorProperty[] = [];
        const customTypeEnumProps: MutatorProperty[] = [];
        const customTypeObjectProps: MutatorProperty[] = [];
        const unknownTypeProps: MutatorProperty[] = [];

        properties.forEach(property => {
            switch (property.type) {
                case "number":
                    numberProps.push(property);
                    break;
                case "string":
                    stringProps.push(property);
                    break;
                case "assetbundle":
                    assetbundleProps.push(property);
                    break;
                default:
                    if (property.type.includes("@")) {
                        customTypeEnumProps.push(property);
                    } else if (property.type.includes("$")) {
                        customTypeObjectProps.push(property);
                    } else {
                        unknownTypeProps.push(property);
                    }
                    break;
            }
        });

        return (
            <Card.Body className="p-2">
                {customTypeEnumProps.length > 0 &&
                    renderPropertyRow(customTypeEnumProps)}
                {customTypeObjectProps.length > 0 &&
                    renderPropertyColumn(customTypeObjectProps)}
                {unknownTypeProps.length > 0 &&
                    renderPropertyRow(unknownTypeProps)}
                {assetbundleProps.length > 0 &&
                    renderPropertyRow(assetbundleProps)}
                {(stringProps.length > 0 || numberProps.length > 0) &&
                    renderPropertyRow([...stringProps, ...numberProps])}
            </Card.Body>
        );
    };

    return (
        <>
            {categories.map(([categoryName, properties], index) => {
                const isOpen = openCategories[categoryName] ?? true;
                return (
                    <Card key={categoryName} className="mb-3">
                        <Card.Header
                            className={`d-flex align-items-center mouseHover`}
                            onClick={() => toggleCategory(categoryName)}
                        >
                            <h5>{categoryName}</h5>
                            <div
                                title={
                                    isOpen
                                        ? `Hide ${categoryName}`
                                        : `Show ${categoryName}`
                                }
                            >
                                <FontAwesomeIcon
                                    className={` ${styles.optionsChevron}`}
                                    size="sm"
                                    icon={isOpen ? faChevronUp : faChevronDown}
                                />
                            </div>
                        </Card.Header>
                        <Collapse in={isOpen}>
                            <div>{renderCategoryBody(properties)}</div>
                        </Collapse>
                    </Card>
                );
            })}
        </>
    );
};

export const MutatorOptions = React.memo(MutatorOptionsImplementation);
